import { FormInstance, Space } from 'antd';
import React, { FC, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { InputNumber } from 'components/atoms/input';
import { RadioGroup } from 'components/atoms/radio-buttons';
import {
  AutomaticLabelWrapper,
  BudgetHoursInputWrapper,
  FormItem as BudgetedHoursSpecificFormItem,
  Hint,
  ManualBudgetedHoursFormItem,
  Radio,
} from 'components/organisms/templates/projects/plan/commons';
import { ProjectTemplateApi } from 'data/templates/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { queueMacrotask } from 'utils/macrotask';
import { Col, Row } from '../styled';
import {
  Label,
  SectionContainer as CommonContainer,
} from './project-fields-step.styles';
import { ProjectFieldsStepFormValues } from './project-fields-step.types';
import { useBudgetedHoursFromTemplates } from './use-budgeted-hours-from-template';

const SectionContainer = styled(CommonContainer)`
  padding-top: 0;
`;

export const FormItem = styled(BudgetedHoursSpecificFormItem)`
  .ant-form-item-label,
  .ant-form-item-label > label {
    width: 100%;
    text-align: left;
    &::after {
      content: '';
    }
  }

  && {
    padding-bottom: 0;
  }
`;

interface Props {
  templates: ProjectTemplateApi[];
  form: FormInstance<ProjectFieldsStepFormValues>;
}

export const BudgetedHoursField: FC<Props> = ({ templates, form }) => {
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);

  const { estimatedBudgetedHours, isBudgetedHoursConfiguredForAnyTemplate } =
    useBudgetedHoursFromTemplates({ templates });

  const budgetedHoursValueChange = useCallback(
    (next) => {
      if (next == null || typeof next !== 'number') {
        form.setFieldsValue({
          budgetedHours: undefined,
          autoBudgetedHours: false,
        });
      } else {
        form.setFieldsValue({
          autoBudgetedHours: false,
          budgetedHours: next,
        });
      }
    },
    [form],
  );
  const inputRef = useRef<HTMLInputElement>(null);

  return isTimesheetFeatureEnabled && !isFinancialsEnabled ? (
    <SectionContainer>
      <Row>
        <Col span={24}>
          {isBudgetedHoursConfiguredForAnyTemplate ? (
            <>
              <FormItem
                name="autoBudgetedHours"
                label={
                  <Label ellipsis={{ tooltip: true }}>
                    <FormattedMessage id="project_hours_budget" />
                  </Label>
                }
              >
                <RadioGroup
                  onChange={() => {
                    if (inputRef.current) {
                      queueMacrotask(() => {
                        inputRef.current?.focus();
                      });
                    }
                  }}
                >
                  <Space direction="vertical">
                    <Radio value>
                      <AutomaticLabelWrapper>
                        <FormattedMessage id="templates.budget_hours.options.automatic.label" />
                        <Hint>
                          <FormattedMessage
                            id="project.create.budgeted_hours.hint"
                            values={{
                              hours: estimatedBudgetedHours,
                              count: templates.length,
                              strong: (chunks) => <strong>{chunks}</strong>,
                            }}
                          />
                        </Hint>
                      </AutomaticLabelWrapper>
                    </Radio>
                    <Radio value={false}>
                      <FormattedMessage id="templates.budget_hours.options.manual.label" />
                    </Radio>
                  </Space>
                </RadioGroup>
              </FormItem>
              <ManualBudgetedHoursFormItem
                shouldUpdate
                label={<FormattedMessage id="project_hours_budget" />}
              >
                {() => (
                  <BudgetHoursInputWrapper
                    hide={form.getFieldValue('autoBudgetedHours') !== false}
                  >
                    <InputNumber
                      ref={inputRef}
                      type="number"
                      min={1}
                      value={form.getFieldValue('budgetedHours')}
                      onChange={budgetedHoursValueChange}
                    />
                  </BudgetHoursInputWrapper>
                )}
              </ManualBudgetedHoursFormItem>
            </>
          ) : (
            <FormItem
              shouldUpdate
              label={
                <Label ellipsis={{ tooltip: true }}>
                  <FormattedMessage id="project_hours_budget" />
                </Label>
              }
            >
              {() => (
                <InputNumber
                  type="number"
                  min={1}
                  value={form.getFieldValue('budgetedHours')}
                  onChange={budgetedHoursValueChange}
                />
              )}
            </FormItem>
          )}
        </Col>
        <FormItem name="budgetedHours" hidden />
      </Row>
    </SectionContainer>
  ) : null;
};
